/**
 * Bara de categorii lipita sub antet, cu urmarirea derularii.
 *
 * Doua lucruri care par simple si nu sunt:
 *
 * 1. **Care categorie e "activa".** Se foloseste `IntersectionObserver` cu o
 *    banda subtire chiar sub barile lipite (`rootMargin`), nu pozitia calculata
 *    la fiecare eveniment de derulare: observatorul nu ruleaza cod pe firul
 *    principal la fiecare pixel, deci derularea ramane fluida si pe un meniu cu
 *    sute de produse. Cand derularea e atat de rapida incat nicio sectiune nu
 *    prinde banda, ramane ultima cunoscuta — mai bine o eticheta putin in urma
 *    decat una care clipeste.
 *
 * 2. **Saltul la o categorie de mai jos.** Sectiunile din afara ecranului au
 *    `content-visibility: auto` (`.da-defer`), deci inaltimea lor e ESTIMATA
 *    pana sunt randate prima oara. Un singur `scrollIntoView` ajunge langa
 *    tinta, nu pe ea: pe drum sectiunile traversate se randeaza si isi iau
 *    inaltimea reala, iar pagina se muta sub tine. De aceea pozitia se corecteaza
 *    cateva cadre, pana se stabilizeaza. Fara asta, "Deserturi" te lasa in
 *    mijlocul altei categorii — cu cat meniul e mai lung, cu atat mai departe.
 */

import { useEffect, useRef, useState } from "react";

export interface CategoryRailItem {
  /** `id` al elementului `<section>` corespunzator din pagina. */
  id: string;
  label: string;
  count: number;
}

/** Cat de aproape de tinta consideram ca saltul a reusit. */
const SETTLE_TOLERANCE_PX = 4;
/** Plafon de cadre pentru corectie: opreste orice bucla, oricat de instabila. */
const SETTLE_MAX_FRAMES = 12;

/**
 * Duce sectiunea sub barile lipite si CORECTEAZA pana pozitia se stabilizeaza.
 *
 * `scroll-margin-top` (`.da-anchor`) tine cont de antet si de bara; ce nu poate
 * el sa acopere e reasezarea produsa de sectiunile care se randeaza in timpul
 * derularii — asta face bucla de mai jos.
 */
function scrollToSection(id: string): void {
  const target = document.getElementById(id);
  if (target === null) return;

  const styles = getComputedStyle(target);
  const offset = Number.parseFloat(styles.scrollMarginTop) || 0;

  let frames = 0;
  const step = (): void => {
    // Tinta poate disparea in cele ~200 ms de corectie (clientul sterge
    // cautarea, navigheaza). Pe un element scos din DOM, `top` e 0, deci
    // diferenta ramane constanta si bucla ar trage pagina in sus la fiecare
    // cadru, pana la plafon — fara nicio tinta.
    if (!target.isConnected) return;
    const current = target.getBoundingClientRect().top - offset;
    if (Math.abs(current) <= SETTLE_TOLERANCE_PX || frames >= SETTLE_MAX_FRAMES) return;
    // Salt instantaneu, nu lin: derularea lina ar lupta cu propriile corectii si
    // ar produce exact tremuratul pe care il reparam.
    window.scrollBy({ top: current, behavior: "auto" });
    frames += 1;
    requestAnimationFrame(step);
  };

  target.scrollIntoView({ block: "start", behavior: "auto" });
  requestAnimationFrame(step);
}

/**
 * Inaltimea combinata a antetului si a barii, in pixeli reali.
 *
 * Se MASOARA, printr-un element sonda cu exact acelasi `calc`, in loc sa se
 * citeasca variabilele si sa se inmulteasca cu 16. Citirea presupune ca valorile
 * sunt scrise in `rem`; in ziua in care cineva scrie `--da-header-h: 56px`,
 * inmultirea da 896 si banda de decizie ajunge in afara ecranului — categoria
 * activa nu se mai schimba niciodata, fara nicio eroare vizibila.
 */
function stickyBarsHeight(): number {
  const probe = document.createElement("div");
  probe.style.position = "absolute";
  probe.style.visibility = "hidden";
  probe.style.pointerEvents = "none";
  probe.style.height = "calc(var(--da-header-h) + var(--da-rail-h))";
  document.body.appendChild(probe);
  const height = probe.getBoundingClientRect().height;
  probe.remove();
  return Math.round(height);
}

/**
 * Categoria vizibila acum sub barile lipite.
 *
 * `ids` se compara pe continut, nu pe referinta: apelantul construieste lista la
 * fiecare randare, iar un efect legat de referinta ar reface observatorul la
 * fiecare tastare in campul de cautare.
 */
export function useActiveSection(ids: string[]): string | null {
  const key = ids.join("|");
  const [active, setActive] = useState<string | null>(ids[0] ?? null);

  /*
    Inaltimea barilor lipite, tinuta separat de lista de sectiuni.

    Doua motive. Se SCHIMBA in timpul vietii paginii: antetul se rupe pe doua
    randuri cand ecranul se ingusteaza, iar `--da-header-h` se rescrie. Legata
    doar de lista de sectiuni, banda de decizie ar fi ramas calculata cu
    inaltimea veche pana la urmatoarea tastare in cautare, deci categoria activa
    s-ar fi schimbat cu zeci de pixeli mai tarziu decat sectiunea de sub bara.
    Si invers: masurata in efectul listei, sonda ar fi fost pusa si scoasa din
    pagina la FIECARE tasta scrisa in cautare, cu reasezare cu tot.
  */
  const [bars, setBars] = useState(0);
  useEffect(() => {
    const measure = (): void => setBars(stickyBarsHeight());
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    const sections = key.length === 0 ? [] : key.split("|");
    if (sections.length === 0) {
      setActive(null);
      return;
    }
    // Cand cautarea schimba lista, categoria activa de dinainte poate sa nu mai
    // existe: se cade pe prima ramasa, nu pe `undefined`.
    setActive((current) => (current !== null && sections.includes(current) ? current : (sections[0] ?? null)));

    // Inainte de prima masuratoare `bars` e 0; se cade pe inaltimea din stiluri
    // ca banda sa nu inceapa din varful ecranului, unde ar alege mereu sectiunea
    // gresita.
    const top = bars > 0 ? bars : stickyBarsHeight();

    const observer = new IntersectionObserver(
      (entries) => {
        // Banda poate prinde doua sectiuni scurte deodata; castiga cea de sus.
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const first = visible[0]?.target.id;
        if (first !== undefined) setActive(first);
      },
      // Banda de decizie: de la marginea de jos a barilor lipite, in jos ~20% din
      // ecran. Restul e ignorat, deci o sectiune lunga nu ramane activa dupa ce
      // urmatoarea i-a luat locul sub bara.
      { rootMargin: `-${top}px 0px -80% 0px`, threshold: 0 },
    );

    for (const id of sections) {
      const element = document.getElementById(id);
      if (element !== null) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [key, bars]);

  return active;
}

export function CategoryRail({ items, active }: { items: CategoryRailItem[]; active: string | null }) {
  const railRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  // Chipul activ se aduce in bara, nu in pagina: `scrollIntoView` obisnuit ar fi
  // derulat si documentul, anuland derularea clientului.
  useEffect(() => {
    const rail = railRef.current;
    const chip = activeRef.current;
    if (rail === null || chip === null) return;
    const left = chip.offsetLeft - rail.clientWidth / 2 + chip.clientWidth / 2;
    // Regula globala `prefers-reduced-motion` din `index.css` acopera doar
    // animatiile si tranzitiile CSS; o derulare lina ceruta din JS trece pe
    // langa ea, deci se verifica explicit.
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    rail.scrollTo({ left: Math.max(0, left), behavior: reduced ? "auto" : "smooth" });
  }, [active]);

  if (items.length < 2) return null;

  return (
    <nav
      aria-label="Categorii din meniu"
      className="sticky z-20 -mx-4 border-b border-line bg-canvas/95 px-4 backdrop-blur"
      style={{ top: "var(--da-header-h)", height: "var(--da-rail-h)" }}
    >
      <div ref={railRef} className="da-rail h-full items-center">
        {items.map((item) => {
          const isActive = item.id === active;
          return (
            <button
              key={item.id}
              ref={isActive ? activeRef : null}
              type="button"
              // `aria-current` spune cititorului de ecran care sectiune e sub
              // bara acum; culoarea singura nu transmite asta.
              aria-current={isActive ? "true" : undefined}
              onClick={() => scrollToSection(item.id)}
              className={[
                "whitespace-nowrap rounded-control px-3 py-1.5 text-sm font-semibold transition-colors",
                isActive
                  ? "bg-brand text-brand-contrast"
                  : "bg-surface text-ink-muted hover:bg-surface-hover hover:text-ink",
              ].join(" ")}
            >
              {item.label}
              <span className="ml-1.5 font-normal opacity-70">{item.count}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
